import {
  ArrowUUpLeft,
  Check,
  Prohibit,
  SealCheck,
  TrashSimple,
  Kanban,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createTaskRepository } from '@/data/repositories/task-repository';
import type { Task, TaskPriority, TaskStatus, Uuid } from '@/data/db';
import { SETTINGS_ID } from '@/data/db/settings.schema';
import { Badge } from '@/components/primitives/badge';
import { Button } from '@/components/primitives/button';
import { Drawer } from '@/components/primitives/dialog';
import { Field, Select, TextArea, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { describeDueSummary } from '@/lib/dates/due-summary';
import { todayLocalDate } from '@/lib/dates/local-date';
import { formatStamp } from '@/lib/dates/timestamps';
import { TaskContactList } from '@/features/contacts/contact-picker';
import { FollowUpTimeline } from '@/features/reminders/follow-up-timeline';
import { AttachmentStrip } from '@/features/attachments/attachment-strip';
import { StickerPicker } from '@/components/stickers/sticker-picker';
import { PROGRESS_CHOICES, ProgressQuickAdd } from './quick-update';
import { RecurrenceEditor } from './recurrence-editor';
import { useBoardColumns, useBoardPlacements, useNotesFor, useTask } from './use-tasks';
import { STATUS_LABELS, dueToneClass } from './task-presentation';

/** 下拉只提供這三個；結束狀態一律走明確的結束操作（data-model 3.2）。 */
const STATUS_OPTIONS: ReadonlyArray<{ value: TaskStatus; label: string }> = [
  { value: 'new', label: STATUS_LABELS.new },
  { value: 'in_progress', label: STATUS_LABELS.in_progress },
  { value: 'waiting', label: STATUS_LABELS.waiting },
];

const PRIORITY_OPTIONS: ReadonlyArray<{ value: TaskPriority; label: string }> = [
  { value: 'normal', label: '一般' },
  { value: 'important', label: '重要' },
  { value: 'urgent', label: '緊急' },
];

interface TaskDetailDrawerProps {
  taskId: Uuid | null;
  onOpenChange: (open: boolean) => void;
  onOpenTask?: (taskId: Uuid) => void;
}

export function TaskDetailDrawer({ taskId, onOpenChange, onOpenTask }: TaskDetailDrawerProps) {
  const { db, save, settings } = useWorkspace();
  const toast = useToast();
  const task = useTask(taskId);
  const notes = useNotesFor(taskId);
  const columns = useBoardColumns();
  const repository = useMemo(() => createTaskRepository(db), [db]);

  const placements = useBoardPlacements();

  // 換一筆任務時才重置草稿，避免自動保存回寫時蓋掉正在輸入的文字。
  const [draft, setDraft] = useState<{ id: string; subject: string; description: string } | null>(
    null,
  );
  if (task && draft?.id !== task.id) {
    setDraft({ id: task.id, subject: task.subject, description: task.description ?? '' });
  }
  const subject = draft?.subject ?? '';
  const description = draft?.description ?? '';

  if (!taskId) return null;

  const isNote = task?.kind === 'note';
  const isFinished = Boolean(task && (task.completedAt || task.cancelledAt));

  async function close() {
    // 關閉抽屜前立即 flush 尚未保存的文字（spec 6.2）。
    await save.flush();
    onOpenChange(false);
  }

  function scheduleText(field: 'subject' | 'description', value: string) {
    if (!taskId) return;
    // 標題是唯一必填欄位（spec 9.1）。清空時不排保存，畫面上以錯誤訊息說明，
    // 不要排一個什麼都不做的保存讓使用者以為存過了。
    if (field === 'subject' && !value.trim()) return;
    save.schedule(`${taskId}:${field}`, async () => {
      if (field === 'subject') {
        await repository.update(taskId, { subject: value.trim() });
      } else {
        await repository.update(taskId, { description: value || null });
      }
    });
  }

  async function runImmediate(operation: () => Promise<void>) {
    try {
      await save.run(operation);
    } catch (error) {
      toast.show({
        message: '沒有存成功',
        detail: error instanceof Error ? error.message : '請再試一次。',
        tone: 'danger',
      });
    }
  }

  const dueSummary = task
    ? describeDueSummary({
        today: todayLocalDate(),
        dueDate: task.dueDate,
        status: task.status,
        waitingSince: task.waitingSince,
      })
    : null;

  const placement = placements?.find((current) => current.taskId === taskId);
  const columnName = columns?.find((column) => column.id === placement?.columnId)?.name;

  return (
    <Drawer
      open={Boolean(taskId)}
      onOpenChange={(next) => {
        if (!next) void close();
      }}
      title={task?.subject ?? '載入中'}
      subtitle={
        task && (
          <>
            <span className="tabular">#{task.number}</span>
            <Badge tone={isNote ? 'neutral' : 'accent'}>{isNote ? '便條' : '工作'}</Badge>
            <Badge tone={statusTone(task)}>{STATUS_LABELS[task.status]}</Badge>
            {dueSummary && (
              <span className={dueToneClass(dueSummary.tone)} title={dueSummary.fullDate}>
                {dueSummary.label}
              </span>
            )}
            {columnName && <span>{columnName}</span>}
          </>
        )
      }
      footer={
        task && (
          <>
            {!isFinished && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<Check size={15} weight="bold" />}
                  onClick={() =>
                    void runImmediate(async () => {
                      await repository.complete(task.id);
                      toast.show({
                        message: '已完成，卡片移入已完成',
                        tone: 'success',
                        action: {
                          label: '復原',
                          onAction: () => void repository.uncomplete(task.id),
                        },
                      });
                    })
                  }
                >
                  完成
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Prohibit size={15} />}
                  onClick={() =>
                    void runImmediate(async () => {
                      await repository.cancel(task.id);
                      toast.show({
                        message: '已標示為不做了',
                        action: {
                          label: '復原',
                          onAction: () => void repository.uncomplete(task.id),
                        },
                      });
                    })
                  }
                >
                  不做了
                </Button>
              </>
            )}

            {task.status === 'resolved' && (
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<SealCheck size={15} />}
                onClick={() => void runImmediate(() => repository.close(task.id))}
              >
                確認結案
              </Button>
            )}

            {isFinished && (
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<ArrowUUpLeft size={15} />}
                onClick={() =>
                  void runImmediate(async () => {
                    await repository.uncomplete(task.id);
                    toast.show({ message: '已回到原本的欄位' });
                  })
                }
              >
                {task.cancelledAt ? '取消「不做了」' : '取消完成'}
              </Button>
            )}

            <span className="flex-1" />

            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Kanban size={15} />}
              onClick={() =>
                void runImmediate(async () => {
                  await repository.removeFromBoard(task.id);
                  toast.show({
                    message: '已從看板移除',
                    detail: '工作紀錄沒有刪除，仍可在任務清單中找到。',
                  });
                })
              }
            >
              移出看板
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<TrashSimple size={15} />}
              onClick={() =>
                void runImmediate(async () => {
                  await repository.softDelete(task.id);
                  toast.show({
                    message: '已移入垃圾桶',
                    action: { label: '復原', onAction: () => void repository.restore(task.id) },
                  });
                  onOpenChange(false);
                })
              }
            >
              刪除
            </Button>
          </>
        )
      }
    >
      {!task ? (
        <p className="text-[0.875rem] text-text-muted">載入中…</p>
      ) : (
        <div className="space-y-5">
          <Field
            label="標題"
            error={subject.trim() ? undefined : '標題是必填的，先留著原本的內容沒有改動。'}
          >
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={subject.trim() ? undefined : true}
                value={subject}
                onChange={(event) => {
                  setDraft((current) =>
                    current ? { ...current, subject: event.target.value } : current,
                  );
                  scheduleText('subject', event.target.value);
                }}
                onBlur={() => void save.flush(`${task.id}:subject`)}
              />
            )}
          </Field>

          {/* 只想補一句「做到哪了」的時候，不必自己在說明裡打時間（spec 13.5）。 */}
          <ProgressQuickAdd
            task={task}
            onSaved={(next) =>
              setDraft((current) => (current ? { ...current, description: next ?? '' } : current))
            }
          />

          <Field label="說明">
            {({ id }) => (
              <TextArea
                id={id}
                value={description}
                placeholder="補充細節、對方的說法、或下一步要做什麼。"
                onChange={(event) => {
                  setDraft((current) =>
                    current ? { ...current, description: event.target.value } : current,
                  );
                  scheduleText('description', event.target.value);
                }}
                onBlur={() => void save.flush(`${task.id}:description`)}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="狀態" hint="做完了與不做了請用下方的按鈕，改這裡不會搬動卡片。">
              {({ id }) => (
                <Select
                  id={id}
                  value={isFinished ? task.status : task.status}
                  onValueChange={(value) =>
                    void runImmediate(() => repository.setStatus(task.id, value as TaskStatus))
                  }
                  options={
                    isFinished
                      ? [{ value: task.status, label: STATUS_LABELS[task.status] }]
                      : STATUS_OPTIONS
                  }
                />
              )}
            </Field>

            <Field label="重要程度">
              {({ id }) => (
                <Select
                  id={id}
                  value={task.priority}
                  onValueChange={(value) =>
                    void runImmediate(() =>
                      repository
                        .update(task.id, { priority: value as TaskPriority })
                        .then(() => {}),
                    )
                  }
                  options={PRIORITY_OPTIONS}
                />
              )}
            </Field>

            <Field label="開始日">
              {({ id }) => (
                <TextInput
                  id={id}
                  type="date"
                  value={task.startDate ?? ''}
                  onChange={(event) =>
                    void runImmediate(() =>
                      repository
                        .update(task.id, { startDate: event.target.value || null })
                        .then(() => {}),
                    )
                  }
                />
              )}
            </Field>

            <Field label="到期日">
              {({ id }) => (
                <TextInput
                  id={id}
                  type="date"
                  value={task.dueDate ?? ''}
                  onChange={(event) =>
                    void runImmediate(() =>
                      repository
                        .update(task.id, { dueDate: event.target.value || null })
                        .then(() => {}),
                    )
                  }
                />
              )}
            </Field>
          </div>

          {!isNote && (
            <fieldset className="space-y-1.5">
              <legend className="text-[0.8125rem] font-medium text-text-muted">完成度</legend>
              <div className="flex flex-wrap items-center gap-1.5">
                {PROGRESS_CHOICES.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    aria-pressed={task.progress === choice}
                    onClick={() =>
                      void runImmediate(() =>
                        repository
                          .update(task.id, { progress: task.progress === choice ? null : choice })
                          .then(() => {}),
                      )
                    }
                    className={
                      task.progress === choice
                        ? 'tabular rounded-full bg-accent px-3 py-1 text-[0.75rem] font-medium text-white'
                        : 'tabular rounded-full bg-surface-sunken px-3 py-1 text-[0.75rem] text-text-muted hover:text-text'
                    }
                  >
                    {choice}%
                  </button>
                ))}
              </div>
              <p className="text-[0.75rem] text-text-muted">點一下設定，再點同一個就取消。</p>
            </fieldset>
          )}

          <StickerPicker
            value={task.stickers}
            recent={settings?.recentStickers ?? []}
            onChange={(stickers) =>
              void runImmediate(() => repository.update(task.id, { stickers }).then(() => {}))
            }
            onUse={(sticker) =>
              void db.settings.update(SETTINGS_ID, {
                recentStickers: [
                  sticker,
                  ...(settings?.recentStickers ?? []).filter((current) => current !== sticker),
                ].slice(0, 20),
              })
            }
          />

          {!isNote && <RecurrenceEditor task={task} />}

          <AttachmentStrip ownerType="task" ownerId={task.id} />

          <TaskContactList taskId={task.id} />

          {!isNote && <FollowUpTimeline taskId={task.id} />}

          {/* 什麼時候加進來的、上次動它是什麼時候（data-model 2.2）。 */}
          <p className="tabular text-[0.75rem] text-text-muted">
            加入於 {formatStamp(task.createdAt)}・最後更新 {formatStamp(task.updatedAt)}
          </p>

          {isNote && task.linkedTaskId && (
            <p className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3 text-[0.8125rem] text-text-muted">
              這是某個工作的補充便條。
            </p>
          )}

          {!isNote && notes && notes.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-[0.8125rem] font-medium text-text-muted">補充便條</h3>
              <ul className="space-y-1.5">
                {notes.map((note) => (
                  <li key={note.id}>
                    <button
                      type="button"
                      onClick={() => onOpenTask?.(note.id)}
                      className="w-full rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-2.5 text-left text-[0.875rem] hover:bg-accent-soft"
                    >
                      <span className="tabular mr-2 text-text-muted">#{note.number}</span>
                      {note.subject}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function statusTone(task: Task): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' {
  if (task.cancelledAt) return 'danger';
  if (task.completedAt) return 'success';
  if (task.status === 'waiting') return 'warning';
  if (task.status === 'in_progress') return 'accent';
  return 'neutral';
}
